<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { getEquipName } from '@/utils/equip'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { equipCollection } from '@/core/equip'
import { getSynthesisPrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { ItemType } from '@/types/item'
import { formatNumberWithCommas } from '@/utils'
import { getDisassembleResStone } from '@/views/dark-gold-workshop/data'

const userStore = useUserStore()

const itemList = computed(() => {
  return userStore.getItemEquipInfoList.filter((item) => {
    if (item.isLock || item.reinforcedLevel || item.starAttrs?.length || item.enchantAttr) return false
    if (!['peerless', 'antiquity', 'unique', 'legend', 'myth'].includes(item.quality)) return false
    if (synthesisIdList.value.includes(item.id)) return false
    if (synthesisItemList.value.length && synthesisItemList.value[0].quality !== item.quality) return false
    return true
  })
})
const synthesisIdList = ref<string[]>([])
const synthesisItemList = computed(() => {
  const res = userStore.getItemEquipInfoList.filter((item) => {
    return synthesisIdList.value.includes(item.id)
  })
  if (res.length !== synthesisIdList.value.length) {
    synthesisIdList.value = res.map((item) => item.id)
  }
  return res
})
const synthesisResItemList = computed<EquipItemType[]>(() => {
  return Object.entries(equipCollection)
    .filter(([equipId, info]) => {
      // if (synthesisItemList.value.length < 20) return false
      const equipIdList = synthesisItemList.value.map((item) => item.equipId)
      const resQuality = synthesisItemList.value[0]?.quality
      if (equipIdList.includes(Number(equipId)) && resQuality !== 'myth') return false
      if (info.quality[0] !== resQuality) return false
      return true
    })
    .map(([equipId, info]) => ({
      ...info,
      id: equipId,
      equipId: Number(equipId),
      isLock: false,
      reinforcedLevel: 0,
      quality: info.quality[0],
    }))
})

const equipInfo = ref<EquipItemType>()
const isShowEquipInfo = ref(false)
async function handleAction(equipItem: EquipItemType, type: 'putIn' | 'takeOut') {
  if (isShowEquipInfo.value) return (equipInfo.value = equipItem)
  const { id, quality } = equipItem
  if (!userStore.warehouseItemList) userStore.warehouseItemList = []
  if (type === 'putIn') {
    if (
      (quality === 'antiquity' && synthesisIdList.value.length >= 3) ||
      (['peerless', 'myth'].includes(quality) && synthesisIdList.value.length >= 5) ||
      synthesisIdList.value.length >= 20
    ) {
      return ElMessage.warning('放入装备材料已达要求')
    }
    if (quality === 'myth' && userStore.consumables.mysteryStone > 0 && !useMysteryStone.value) {
      useMysteryStone.value = true
    }
    synthesisIdList.value.push(id)
  } else {
    const index = synthesisIdList.value.findIndex((item) => item === id)
    if (index === -1) return
    synthesisIdList.value.splice(index, 1)
  }
}

const needCount = computed(() => {
  const synthesisQulity = synthesisItemList.value[0]?.quality
  if (synthesisQulity === 'peerless' || synthesisQulity === 'myth') {
    return 5 - synthesisIdList.value.length
  } else if (synthesisQulity === 'antiquity') {
    return 3 - synthesisIdList.value.length
  } else {
    return 20 - synthesisIdList.value.length
  }
})
const isStart = ref(false)
function handleStart() {
  const synthesisQulity = synthesisItemList.value[0]?.quality
  if (synthesisQulity === 'antiquity') {
    if (userStore.diamond < 100) return ElMessage.warning('钻石不足')
    return ElMessageBox.confirm(
      `<p class="affix">您确定要合成【<span class="antiquity">1 远古石</span>】吗？该操作将导致您材料台当中的装备<span class="hp">全部销毁</span>，请务必谨慎（本次合成消耗 <span class="diamond">100钻石</span>）</p>`,
      '提示',
      {
        type: 'warning',
        dangerouslyUseHTMLString: true,
        appendTo: 'body',
        confirmButtonClass: 'is-plain',
      },
    )
      .then(async () => {
        await socketEmitAsync({
          type: 'synthesisAntiquityStone',
          data: {
            idList: synthesisIdList.value,
          },
        })
        for (const id of synthesisIdList.value) {
          const index = userStore.itemList.findIndex((item) => item.id === id)
          userStore.itemList.splice(index, 1)
        }
        synthesisIdList.value = []
        userStore.consumables.antiquityStone++
        userStore.diamond -= 100
        ElMessage.success('合成成功，获得 1远古石')
      })
      .catch(() => {})
  }
  if (userStore.diamond < getSynthesisPrice(synthesisItemList.value[0]?.quality)) return ElMessage.warning('钻石不足')
  isStart.value = true
}
function handleClear() {
  synthesisIdList.value = []
  isStart.value = false
}
const useMysteryStone = ref(false)
function handleChangeUseMysteryStone() {
  if (!useMysteryStone.value && userStore.consumables.mysteryStone <= 0) {
    ElMessage.warning('奥秘锻造石不足')
    return false
  }
  return true
}
function handleSynthesis(equipItem: EquipItemType) {
  if (isShowEquipInfo.value) return (equipInfo.value = equipItem)

  ElMessageBox.confirm(
    `<p class="affix">您确定要合成【<span class="${equipItem.quality}">${getEquipName(
      equipItem,
      equipItem.type,
      false,
    )}</span>】吗？该操作将导致您材料台当中的装备<span class="hp">全部销毁</span>，请务必谨慎（本次合成消耗 <span class="diamond">${getSynthesisPrice(
      equipItem.quality,
    )}钻石</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<ItemType>({
        type: 'synthesisEquip',
        data: {
          idList: synthesisIdList.value,
          equipId: equipItem.equipId,
          useMysteryStone: useMysteryStone.value,
        },
      })
      if (useMysteryStone.value) userStore.consumables.mysteryStone--
      synthesisItemList.value.forEach((item) => {
        if (item.quality !== 'myth') return
        const refineLevel = item.refineAttr?.[0]
        if (refineLevel) {
          userStore.consumables.refineStone += getDisassembleResStone(refineLevel)
        }
      })

      userStore.itemList.unshift(res)
      for (const id of synthesisIdList.value) {
        const index = userStore.itemList.findIndex((item) => item.id === id)
        userStore.itemList.splice(index, 1)
      }
      userStore.diamond -= getSynthesisPrice(equipItem.quality)

      isStart.value = false
      useMysteryStone.value = false
      synthesisIdList.value = []

      ElMessage.success('合成成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="synthesis">
    <p class="header">
      <el-popover placement="bottom" width="100%" trigger="click">
        <template #reference>
          <el-icon size="18"><QuestionFilled /></el-icon>
        </template>
        <div class="affix">
          <h4>合成说明</h4>
          <p>
            1. 消耗 <span class="grow">20</span>件同品质装备，您可以花费<span class="diamond">钻石</span>指定合成 1件<span class="info"
              >任意同品质装备</span
            >。
          </p>
          <p>2. 您需要将装备放入<span class="info">材料台</span>内，而后点击<span class="info">合成</span>，即可在左侧选择您希望合成的装备。</p>
          <p>
            3. 仅有<span class="unique">独特</span>、<span class="legend">传说</span>、<span class="antiquity">远古</span>、<span class="peerless"
              >绝世</span
            >的装备支持合成，同时，您无法将<span class="info">已锁定、已强化、已升星、已附魔</span>的装备放入材料台。
          </p>
          <p>4. 放入<span class="antiquity">远古</span>装备时，为合成 <span class="antiquity">远古石</span>，需放入 3件装备。</p>
          <p>5. 放入<span class="peerless">绝世</span>、<span class="myth">神话暗金</span>装备时，需放入 5件装备。</p>
          <p>6. 合成<span class="myth">神话暗金</span>装备时，将返还对其使用的<span class="darkGold">精造石</span>。</p>
          <p>7. 装备属性会随版本更迭持续变化，请务必谨慎操作，作为合成材料的装备将被<span class="hp">销毁</span>。</p>
        </div>
      </el-popover>
      <span>💎 {{ formatNumberWithCommas(userStore.diamond) }}</span>
    </p>
    <div class="main">
      <div class="item-list">
        <div class="border-wrap">
          <h4>{{ isStart ? '选择合成的装备' : '背包' }}</h4>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px">查看装备信息：</span>
            <el-switch v-model="isShowEquipInfo" size="small" @change="equipInfo = undefined" />
          </div>
          <div class="item-btn-wrap btn-wrap" v-show="!isStart">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of itemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'putIn')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
          <div class="item-btn-wrap btn-wrap" v-show="isStart">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of synthesisResItemList" :key="item.id">
                <CommonBtn size="small" @done="handleSynthesis(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
        <div class="border-wrap" style="padding: 10px; min-height: 50%; margin-top: 5px" v-show="isShowEquipInfo">
          <h4>装备信息</h4>
          <template v-if="equipInfo">
            <EquipInfo :equip-info="equipInfo" :show-action="false" :show-lock="false" />
          </template>
        </div>
      </div>
      <div class="item-list">
        <div class="action border-wrap">
          <h4>操作</h4>
          <span v-show="synthesisItemList?.[0]?.quality === 'myth'" style="margin-bottom: 5px">
            <span style="font-size: 12px">使用奥秘锻造石（{{ userStore.consumables.mysteryStone }}）：</span>
            <el-switch v-model="useMysteryStone" size="small" :before-change="handleChangeUseMysteryStone" />
          </span>
          <el-button type="primary" :disabled="Boolean(needCount)" plain size="small" @click="handleStart" v-show="!isStart">
            合成{{ needCount > 0 ? `（还需${needCount}件）` : '' }}
          </el-button>
          <el-button type="danger" plain size="small" @click="isStart = false" v-show="isStart">取消合成</el-button>
          <el-button type="warning" plain size="small" @click="handleClear">清空材料台</el-button>
          <p style="font-size: 14px; margin: 0">合成花费：{{ getSynthesisPrice(synthesisItemList?.[0]?.quality) }}💎</p>
        </div>
        <div class="border-wrap">
          <h4>材料台</h4>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of synthesisItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'takeOut')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.synthesis {
  padding-top: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .header {
    display: flex;
    justify-content: space-between;
    margin: 0 0 5px 0;
    padding: 0 15px;
    font-size: 14px;
  }
  .item-filter {
    margin-bottom: 10px;
    display: flex;
    .el-button {
      margin-left: 10px;
    }
  }
  .main {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    flex: 1;
  }
  .item-list {
    width: calc(50% - 5px);
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .border-wrap {
      overflow: hidden;
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
      &.action {
        height: auto;
        margin-bottom: 10px;
        padding: 0 10px 10px 10px;
        flex-shrink: 0;
        .el-button {
          margin-bottom: 10px;
          margin-left: 0;
        }
      }
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
